<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import NavMore from '@/Components/Public/NavMore.vue'

// The brand's one header navigation, shared by every public surface —
// the main site and the satellite hosts (invest, painting, partners), which
// used to show only a lockup and a language picker (2026-09-09). Two shapes of
// the same links: `inline` for the desktop bar, `menu` for the phone burger.
//
// Everything comes from shared props, so the component works on any host:
// the main site shares `public_hosts` (with `current`), the satellites share
// the same map under `site_footer.hosts`. Without `current` every link is
// absolute, which is exactly right when we're on invest/painting/partners.
const props = defineProps({
  variant: { type: String, default: 'inline' }, // inline | menu
  // Set on the satellite hosts (invest / painting / partners). They inherit
  // Public::BaseController's shared props, where PublicHosts.kind_for reads
  // their host as :apex — true for canonicals and OG images, but it would make
  // the apex links relative here, and /charters does not exist on
  // invest.getphuket.com.
  external: { type: Boolean, default: false },
})

const page = usePage()
const { t } = useI18n()

const current = computed(() => page.props.locale || 'en')
const hosts = computed(() => page.props.public_hosts || page.props.site_footer?.hosts || {})
const footer = computed(() => page.props.site_footer || {})

const investUrl = computed(() => footer.value.invest_url || satellite('invest'))
const paintingUrl = computed(() => footer.value.painting_url || 'https://painting.getphuket.com')
const partnersBase = computed(() => footer.value.partners_base || page.props.partners_base || 'https://partners.getphuket.com')

// invest is the only satellite with a .ru twin; follow the domain we're on.
function satellite(name) {
  const apex = hosts.value.apex || ''
  if (apex.startsWith('/')) return `${apex === '/_p' ? '/_i' : apex}`
  return apex.endsWith('.ru') ? `https://${name}.getphuket.ru` : `https://${name}.getphuket.com`
}

// Absolute link into another public host, locale prefix preserved.
function hostPath(kind, path) {
  const base = hosts.value[kind] || ''
  if (base.startsWith('/')) return `${base}${path === '/' ? '' : path}`
  const loc = current.value
  const prefix = (!loc || loc === 'en') ? '' : `/${loc}`
  return `${base}${prefix}${path === '/' ? '' : path}` || '/'
}

// Apex sections stay relative while we're on the apex; from anywhere else
// (a vertical subdomain, or invest/painting/partners) they go home.
function apexPath(path) {
  if (props.external || hosts.value.current !== 'apex') return hostPath('apex', path)
  const loc = current.value
  return (!loc || loc === 'en') ? path : `/${loc}${path === '/' ? '' : path}`
}

const vipLabel = computed(() => t('public.vip.nav'))
</script>

<template>
  <nav v-if="props.variant === 'inline'" class="public-nav">
    <a :href="apexPath('/charters')" class="public-nav__link">{{ t('public.nav.charters') }}</a>
    <a :href="hostPath('villas', '/')" class="public-nav__link">{{ t('public.nav.villas') }}</a>
    <a :href="hostPath('transfers', '/')" class="public-nav__link">{{ t('public.nav.transfers') }}</a>
    <a :href="hostPath('experiences', '/')" class="public-nav__link">{{ t('public.nav.tours') }}</a>
    <a :href="hostPath('rentals', '/')" class="public-nav__link">{{ t('public.nav.rentals') }}</a>
    <a :href="apexPath('/vip')" class="public-nav__link public-nav__link--vip">{{ vipLabel }}</a>
    <a :href="apexPath('/plan')" class="public-nav__link public-nav__link--concierge">{{ t('public.nav.concierge') }}</a>
    <NavMore :label="t('public.nav.more')">
      <a :href="apexPath('/destinations')">{{ t('public.nav.destinations') }}</a>
      <a :href="apexPath('/occasions')">{{ t('public.occasions.eyebrow') }}</a>
      <a :href="investUrl">{{ t('public.nav.invest') }}</a>
      <a :href="paintingUrl">{{ t('public.nav.painting') }}</a>
      <a :href="partnersBase">{{ t('public.footer.list_your_business') }}</a>
    </NavMore>
  </nav>

  <!-- Phone: the inline bar is hidden below 640px, this takes over. -->
  <HeaderMenu v-else class="public-nav-burger" :aria-label="t('public.menu.nav')">
    <template #icon>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </template>
    <template #default="{ close }">
      <a :href="apexPath('/charters')" class="hmenu__item" @click="close()">{{ t('public.nav.charters') }}</a>
      <a :href="hostPath('villas', '/')" class="hmenu__item" @click="close()">{{ t('public.nav.villas') }}</a>
      <a :href="hostPath('transfers', '/')" class="hmenu__item" @click="close()">{{ t('public.nav.transfers') }}</a>
      <a :href="hostPath('experiences', '/')" class="hmenu__item" @click="close()">{{ t('public.nav.tours') }}</a>
      <a :href="apexPath('/vip')" class="hmenu__item hmenu__item--vip" @click="close()">{{ vipLabel }}</a>
      <a :href="apexPath('/plan')" class="hmenu__item" @click="close()">{{ t('public.nav.concierge') }}</a>
      <a :href="apexPath('/destinations')" class="hmenu__item" @click="close()">{{ t('public.nav.destinations') }}</a>
      <a :href="apexPath('/occasions')" class="hmenu__item" @click="close()">{{ t('public.occasions.eyebrow') }}</a>
      <a :href="hostPath('rentals', '/')" class="hmenu__item" @click="close()">{{ t('public.nav.rentals') }}</a>
      <a :href="investUrl" class="hmenu__item" @click="close()">{{ t('public.nav.invest') }}</a>
      <a :href="paintingUrl" class="hmenu__item" @click="close()">{{ t('public.nav.painting') }}</a>
      <a :href="partnersBase" class="hmenu__item hmenu__item--list" @click="close()">{{ t('public.footer.list_your_business') }}</a>
    </template>
  </HeaderMenu>
</template>

<style scoped>
.public-nav {
  display: none;
  align-items: center;
  gap: 1.25rem;
  margin-right: auto;
  margin-left: 2rem;
  font-size: 0.9375rem;
}
@media (min-width: 640px) { .public-nav { display: inline-flex; } }
.public-nav__link {
  color: var(--inkSoft);
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
}
/* Seven items + the concierge pill: tighten before anything has to wrap. */
@media (max-width: 1180px) { .public-nav { gap: 0.9rem; margin-left: 1.25rem; font-size: 0.875rem; } }
@media (max-width: 1024px) { .public-nav { gap: 0.7rem; margin-left: 1rem; font-size: 0.8375rem; } .public-nav__link--concierge { padding: 0.4rem 0.75rem; font-size: 0.64rem; } }
.public-nav__link:hover { color: var(--ink); }
/* VIP is the emphasized premium segment — gold accent so it stands out in the
   nav rather than blending in with the standard product links. */
.public-nav__link--vip { color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); font-weight: 700; letter-spacing: 0.02em; }
.public-nav__link--vip:hover { color: var(--gold, var(--warn)); }
:deep(.hmenu__item--vip) { color: color-mix(in oklch, var(--gold, var(--warn)) 62%, var(--ink)); font-weight: 700; }
/* Concierge — the one action in the header: a small night pill with the gold
   hairline, same voice as LuxButton. */
.public-nav__link--concierge {
  margin-left: 0.25rem; padding: 0.45rem 0.95rem; border-radius: 999px;
  background: var(--night, var(--ink)); color: var(--foam, #ECF3F6);
  border: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 55%, transparent);
  font-size: 0.7rem; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; line-height: 1;
  transition: border-color 150ms ease, background 150ms ease;
}
.public-nav__link--concierge:hover { color: var(--foam, #ECF3F6); background: var(--night2, var(--ink)); border-color: var(--gold, var(--warn)); }
:deep(.hmenu__item--list) { color: var(--accent); font-weight: 700; }

.public-nav-burger { display: inline-flex; }
@media (min-width: 640px) { .public-nav-burger { display: none; } }
/* The dropdown items are slot-projected into HeaderMenu's panel; both header
   shells style them too, this keeps the burger right on any future surface. */
.public-nav-burger :deep(.hmenu__item) {
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  width: 100%; padding: 0.55rem 0.75rem; background: transparent; border: 0;
  border-radius: 0.4rem; color: var(--ink); font: inherit; font-size: 0.875rem;
  text-align: left; text-decoration: none; cursor: pointer; transition: background 0.1s;
}
.public-nav-burger :deep(.hmenu__item:hover) { background: color-mix(in oklch, var(--ink) 6%, transparent); }
</style>
